/**
 * Popup coordinator.
 *
 * WHY THIS EXISTS: the three interruptive surfaces (cookie consent, email
 * capture, local-service-area) each had their own independent timer — 1s, 10s
 * and 5s — and no knowledge of one another. On a first visit all three appeared
 * and stayed, stacked on top of the hero, leaving the page unusable until the
 * visitor dismissed each one. a large online retailer, by comparison, shows exactly one
 * interruption with a "No thanks, maybe later" opt-out.
 *
 * Rules enforced here:
 *  1. Cookie consent is legally required and always goes FIRST, alone.
 *  2. Nothing promotional appears until consent has been answered.
 *  3. At most ONE promotional popup per page view — first to claim wins.
 *
 * Deliberately module-scoped (not React state): these components are siblings
 * with no shared parent, and a context provider would force the whole tree to
 * re-render on every popup change.
 */

import { safeStorageGet, safeStorageSet, safeStorageRemove } from '@/lib/ui/safeStorage';

/**
 * Is it OK for a promotional popup to appear?
 *
 * Always true since 2026-10-08: the cookie banner was removed from the root
 * layout, so nothing writes `cookie-consent` any more and a real check would
 * read "not answered" forever — silently suppressing every promo.
 *
 * Kept as a function (rather than deleting the call sites) because it is the
 * one place to re-introduce a gate if a consent surface ever comes back: put
 * the check here and every popup picks it up. The previous implementation read
 * the key and, when storage was unreadable, treated it as settled so a visitor
 * could never be trapped behind a banner that would never read as dismissed.
 */
export function consentSettled(): boolean {
  return true;
}

/**
 * Routes where NO promotional popup may ever appear: the visitor is in the
 * middle of configuring or paying for an order, and an interruption there
 * costs a sale rather than capturing one.
 *
 * Single source of truth. This used to live as an inline regex inside
 * EmailCapturePopup only, so the exit-intent and local-appointment popups —
 * added later — happily covered the configurator, cart and checkout. A
 * customer reported a popup they could not get past while trying to order.
 *
 * Add new interruptive surfaces to this check, not to their own copy of it.
 */
const ORDER_FLOW = /^\/(products\/configure|cart|checkout)(\/|$)/;

export function isOrderFlowRoute(pathname?: string): boolean {
  if (typeof window === 'undefined') return true; // never pre-render a popup open
  return ORDER_FLOW.test(pathname ?? window.location.pathname);
}

/**
 * Storage for the popups' "show this only once" guards.
 *
 * Delegates to the shared safeStorage helper so there is exactly ONE in-memory
 * mirror in the app: if a popup wrote a dismissal through one module and read
 * it back through another, the guard would miss and the popup would return.
 *
 * Why it matters here specifically: these guards are the only thing stopping a
 * popup whose timer re-arms from reappearing. When localStorage cannot persist,
 * a raw read returns null forever, the guard reads "never shown", and the
 * visitor gets the popup again every time the timer fires.
 */
export function popupStorageGet(key: string): string | null {
  return safeStorageGet(key);
}

export function popupStorageSet(key: string, value: string): void {
  safeStorageSet(key, value);
}

export function popupStorageRemove(key: string): void {
  safeStorageRemove(key);
}

// Which promotional popup currently owns the screen, if any.
let activePromo: string | null = null;

const listeners = new Set<() => void>();

/** Notify subscribers that the slot changed (e.g. a popup closed). */
function emit() {
  listeners.forEach((l) => {
    try { l(); } catch { /* a broken listener must not block the others */ }
  });
}

/**
 * Has ANY promotional popup already had its turn? Rule 3 below says at most one
 * per page view, but that was only half-enforced: `activePromo` tracked who was
 * on screen *right now*, and releasing it on dismiss handed the slot to the next
 * popup in the same page view. A visitor who sat on a category page therefore
 * got the email-capture offer at 10s, dismissed it, and the exit-intent offer at
 * 45s — two separate modals, which reads as "a popup that keeps coming".
 * Reproduced on the live build before this latch existed.
 *
 * Module-scoped, so it also holds across client-side navigation; each popup
 * still keeps its own longer-lived localStorage guard for repeat visits.
 */
let promoShownThisPageView = false;

/**
 * Try to take the single promotional slot.
 * Returns true if this popup may show. Idempotent for the same owner, so a
 * re-render can't cause a component to lose a slot it already holds.
 */
export function claimPromoSlot(owner: string): boolean {
  if (activePromo === owner) return true;       // same owner re-rendering
  if (activePromo !== null) return false;       // another popup is on screen
  if (promoShownThisPageView) return false;     // one promo per page view, period
  activePromo = owner;
  promoShownThisPageView = true;
  return true;
}

/**
 * Release the slot on close, so a mounted-but-closed popup isn't holding it.
 * Note this does NOT re-open the gate: `promoShownThisPageView` stays latched,
 * so releasing cannot hand the screen to the next popup in the same page view.
 */
export function releasePromoSlot(owner: string): void {
  if (activePromo === owner) {
    activePromo = null;
    emit();
  }
}

/** True when no promotional popup is on screen. */
export function promoSlotFree(): boolean {
  // "Free" must mean a promo could actually claim it — not merely that nothing
  // is on screen — or a caller would read a latched slot as available.
  return activePromo === null && !promoShownThisPageView;
}

/** Subscribe to slot changes; returns an unsubscribe function. */
export function onPromoSlotChange(cb: () => void): () => void {
  listeners.add(cb);
  return () => listeners.delete(cb);
}
